/**
 * GSTR-3B — the statutory form, as the legacy renders it (ADR-0021).
 *
 * The legacy lays 3B out as **Form GSTR-3B, [See rule 61(5)]** with its real
 * section numbering, and a practitioner reads it the way they read the paper
 * return. A flat list of cell ids is a debugging view; this is the form.
 *
 * **Read-only, and that is the design.** The service derives every cell (W2-05:
 * the 88A convention, carry-forward, the 4B floor); a screen that let someone
 * type into 3B would be deciding a tax liability, which ADR-0014 forbids. The
 * legacy agrees — its 3B is read-only bar one ITC bucket control.
 *
 * **A gated cell renders as its state, never as ₹0.00.** `pending_verification`,
 * `not_computed`, `decided_outside` and `declined` are not zero: zero is a
 * claim that the value is nil, and
 * on 3.1(b) — zero-rated supplies — that claim is the difference between an
 * exporter's refund and a wrong return. The legacy shows a blank or a dash and
 * lets the operator fill it; we name *why* it is dark and what it waits on.
 */

import { Alert } from '@fiveox/ui'
import { Table2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { ApiError, type Gstr3bBody, type PeriodRecord, periods } from '../lib/api'
import { carriedForward } from '../lib/carriedForward'
import { perHeadNet } from '../lib/perHeadNet'
import { unmatchedCreditNotes } from '../lib/unmatchedCreditNotes'
import { useClient } from './ClientLayout'

const HEADS = ['igst', 'cgst', 'sgst', 'cess'] as const
type Head = (typeof HEADS)[number]

const HEAD_LABELS: Record<Head, string> = {
  igst: 'Integrated tax',
  cgst: 'Central tax',
  sgst: 'State/UT tax',
  cess: 'Cess',
}

/** The statutory wording of 3.1, verbatim — an operator matches on these. */
const OUTWARD_ROWS: Array<{ id: string; label: string }> = [
  { id: '3.1(a)', label: '(a) Outward taxable supplies (other than zero rated, nil rated and exempted)' },
  { id: '3.1(b)', label: '(b) Outward taxable supplies (zero rated)' },
  { id: '3.1(c)', label: '(c) Other outward supplies (nil rated, exempted)' },
  { id: '3.1(d)', label: '(d) Inward supplies (liable to reverse charge)' },
  { id: '3.1(e)', label: '(e) Non-GST outward supplies' },
]

const ITC_ROWS: Array<{ id: string; label: string }> = [
  { id: '4A', label: '(A) ITC Available (whether in full or part)' },
  { id: '4B', label: '(B) ITC Reversed' },
]

function rupees(paise: unknown): string {
  if (typeof paise !== 'number') return '—'
  return (paise / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

type Cell = Record<string, unknown> | undefined

/** A cell's state, when it has one instead of a value. */
function stateOf(cell: Cell): string | null {
  const s = cell?.state
  return typeof s === 'string' ? s : null
}

const STATE_LABELS: Record<string, string> = {
  pending_verification: 'Awaiting verification',
  not_computed: 'Not computed',
  // V-d was answered on 2026-09-10 (Q2) and the answer was that this app does
  // not compute the Rule 42/43 reversal. "Not computed" read as an unfinished
  // job; this says a person decides it and enters it elsewhere, which is the
  // whole difference between a gap and a decision.
  decided_outside: 'Decided outside ProGST',
  // V-h, same sitting (Q3): eligible ITC never runs ahead of 2B. There is no
  // verification left to await.
  declined: 'Not claimed — matched 2B only',
  // X-08. Named for the input that has not arrived, not for a judgement: there
  // is nothing to verify and nothing has been declined. Under V-h the credit
  // with no 2B is arithmetically zero, but printing 0.00 would assert the
  // client had none — so the cell says which it is.
  awaiting_2b: '2B not imported for this period',
}

/** Why a cell is dark, in the service's own words. */
function gateOf(cell: Cell): string | null {
  const src = cell?.source as Record<string, unknown> | undefined
  const text = src?.gate ?? src?.note
  return typeof text === 'string' ? text : null
}

function CellValue({ cell, head }: { cell: Cell; head: Head | 'taxable' }) {
  const state = stateOf(cell)
  if (state) {
    // Deliberately not a number. Rendering 0.00 here would assert nil.
    return <span className="gst-chip is-hold">{STATE_LABELS[state] ?? state}</span>
  }
  const value = cell?.[head]
  return <>{rupees(value)}</>
}

export function Gstr3b() {
  const client = useClient()
  const [rows, setRows] = useState<PeriodRecord[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [body, setBody] = useState<Gstr3bBody | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)

  useEffect(() => {
    // Scoped to the client in the URL (ND-C1). Before this, every screen
    // listed every client's periods together and the rail asked you to pick
    // one out of the pile — which is the shape of a wrong-client mistake.
    void periods
      .list({ client_id: client.id })
      .then((b) => {
        setRows(b.data)
        // NOT `cur ?? …`: the effect re-runs when the client changes, and
        // keeping the previous selection would leave another client's period
        // on screen. Right numbers, wrong client is the mistake this whole
        // slice exists to make impossible.
        setSelected(b.data[0]?.id ?? null)
      })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Returns could not be loaded.'),
      )
  }, [client.id])

  const load = useCallback(async () => {
    if (!selected) {
      setBody(null)
      return
    }
    try {
      setBody((await periods.gstr3b(selected)).data)
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The 3B could not be derived.')
    }
  }, [selected])

  useEffect(() => {
    void load()
  }, [load])

  const cells = (body?.gstr3b?.cells ?? {}) as Record<string, Cell>
  const setoff = body?.gstr3b?.setoff as
    | {
        cash?: Record<string, number>
        utilisation?: Record<string, Record<string, number>>
        credit_remaining?: Record<string, number>
      }
    | undefined
  const netView = perHeadNet(cells['6.1'])

  return (
    <div>
      <header className="mb-6">
        <h1 className="gst-h1">
          <Table2 size={20} /> Form GSTR-3B
        </h1>
        <p className="gst-sub">[See rule 61(5)] — derived from the same draft GSTR-1 reads</p>
      </header>

      {error && <Alert tone="error" className="mb-4">{error}</Alert>}

      <div className="flex flex-wrap gap-6">
        <aside className="w-64 shrink-0">
          {rows.length === 0 ? (
            <div className="gst-empty">No periods yet.</div>
          ) : (
            <ul className="space-y-1">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(row.id)}
                    aria-current={row.id === selected}
                    className="gst-rail-item"
                  >
                    <span className="truncate">{row.return_type}</span>
                    <span className="mt-0.5 block text-[11px]">{row.period}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="min-w-0 flex-1">
          {!body ? (
            <p className="gst-sub">Select a period.</p>
          ) : (
            <>
              {/* -- the header block the form carries ----------------------- */}
              <div className="gst-table-wrap mb-4">
                <table className="gst-table">
                  <tbody>
                    <tr>
                      <td className="is-text is-sec">Year</td>
                      <td className="is-text">{body.gstr3b.period?.split('-')[1] ?? '—'}</td>
                      <td className="is-text is-sec">Period</td>
                      <td className="is-text">{body.gstr3b.period}</td>
                    </tr>
                    <tr className="is-a">
                      <td className="is-text is-sec">GSTIN of the supplier</td>
                      <td className="is-text" colSpan={3}>
                        <span className="gst-mono">{body.client_gstin ?? "—"}</span>
                      </td>
                    </tr>
                    <tr>
                      <td className="is-text is-sec">2(a). Legal name of the registered person</td>
                      <td className="is-text" colSpan={3}>
                        {body.client_name}
                      </td>
                    </tr>
                    {/* ARN is assigned by the portal on filing. Blank until then,
                        as on the form — not a value we can invent. */}
                    <tr className="is-a">
                      <td className="is-text is-sec">2(c). ARN</td>
                      <td className="is-text">{body.period.filed_ack_no ?? '—'}</td>
                      <td className="is-text is-sec">2(d). Date of ARN</td>
                      <td className="is-text">—</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="gst-meta mb-3">(Amount in ₹ for all tables)</p>

              {/* -- 3.1 ---------------------------------------------------- */}
              <h3 className="gst-sub mb-2">
                3.1 Details of Outward supplies and inward supplies liable to reverse charge
              </h3>
              <div className="gst-table-wrap mb-5">
                <table className="gst-table">
                  <thead>
                    <tr>
                      <th className="is-text">Nature of Supplies</th>
                      <th>Total taxable value</th>
                      {HEADS.map((h) => (
                        <th key={h}>{HEAD_LABELS[h]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {OUTWARD_ROWS.map((row, i) => {
                      const cell = cells[row.id]
                      const gate = gateOf(cell)
                      const expandable = !!cell && !stateOf(cell)
                      return (
                        <>
                          <tr
                            key={row.id}
                            className={i % 2 === 1 ? 'is-a' : ''}
                            onClick={() => expandable && setOpen(open === row.id ? null : row.id)}
                            style={expandable ? { cursor: 'pointer' } : undefined}
                          >
                            <td className="is-text is-sec">
                              {row.label}
                              {gate && <small>{gate}</small>}
                            </td>
                            <td>
                              <CellValue cell={cell} head="taxable" />
                            </td>
                            {HEADS.map((h) => (
                              <td key={h}>
                                <CellValue cell={cell} head={h} />
                              </td>
                            ))}
                          </tr>
                          {/* The legacy expands a row into where its number came
                              from. Ours shows the service's own provenance. */}
                          {open === row.id && cell?.source != null && (
                            <tr key={`${row.id}-src`}>
                              <td className="is-text" colSpan={6}>
                                <span className="gst-meta">
                                  Source:{' '}
                                  {String(
                                    (cell.source as Record<string, unknown>).from ??
                                      (cell.source as Record<string, unknown>).note ??
                                      '—',
                                  )}
                                </span>
                              </td>
                            </tr>
                          )}
                        </>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* -- 3.2 ---------------------------------------------------- */}
              <h3 className="gst-sub mb-2">
                3.2 Out of supplies made in 3.1(a), details of inter-state supplies
              </h3>
              <div className="gst-table-wrap mb-5">
                <table className="gst-table">
                  <thead>
                    <tr>
                      <th className="is-text">Nature of Supplies</th>
                      <th>Total taxable value</th>
                      <th>Integrated tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="is-text is-sec">
                        Supplies made to unregistered persons
                        {gateOf(cells['3.2']) && <small>{gateOf(cells['3.2'])}</small>}
                      </td>
                      <td>
                        <CellValue cell={cells['3.2']} head="taxable" />
                      </td>
                      <td>
                        <CellValue cell={cells['3.2']} head="igst" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* -- 4 ------------------------------------------------------ */}
              <h3 className="gst-sub mb-2">4. Eligible ITC</h3>
              {/* D-31. These notes reduce nothing in 4(A) by the owner's ruling,
                  which is exactly why they are said here, beside the figures
                  they qualify, rather than left in a provenance line nobody
                  opens. */}
              {(() => {
                const flag = unmatchedCreditNotes(cells['4A'])
                if (!flag) return null
                return (
                  <Alert tone="warning" className="mb-3">
                    {flag.count} credit {flag.count === 1 ? 'note' : 'notes'} in
                    this period&apos;s 2B matched no purchase document, so{' '}
                    {flag.count === 1 ? 'it reduces' : 'they reduce'} nothing
                    below. The credit {flag.count === 1 ? 'it' : 'they'} would
                    reverse is still counted in 4(A)
                    {flag.amounts.length > 0 && (
                      <>
                        {' '}
                        —{' '}
                        {flag.amounts
                          .map((a) => `${HEAD_LABELS[a.head]} ${rupees(a.paise)}`)
                          .join(', ')}
                      </>
                    )}
                    . Match or suppress {flag.count === 1 ? 'it' : 'them'} in
                    Reconciliation.
                  </Alert>
                )
              })()}
              {/* D-38. Credit in 4(A) this month that was booked in an earlier
                  one — said beside the figure, because a practitioner comparing
                  this month's register with 4(A) would otherwise find more
                  credit than the month's purchases. */}
              {(() => {
                const carried = carriedForward(cells['4A'])
                if (!carried) return null
                return (
                  <Alert tone="info" className="mb-3">
                    {`4(A) includes credit on ${carried.count} ${
                      carried.count === 1 ? 'purchase' : 'purchases'
                    } booked in an earlier month (${carried.fromPeriods
                      .map((p) => `${p.count} from ${p.period}`)
                      .join(', ')}) that matched this month's 2B${
                      carried.amounts.length > 0
                        ? ` — ${carried.amounts
                            .map((a) => `${HEAD_LABELS[a.head]} ${rupees(a.paise)}`)
                            .join(', ')}`
                        : ''
                    }.`}
                  </Alert>
                )
              })()}
              <div className="gst-table-wrap mb-5">
                <table className="gst-table">
                  <thead>
                    <tr>
                      <th className="is-text">Details</th>
                      {HEADS.map((h) => (
                        <th key={h}>{HEAD_LABELS[h]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ITC_ROWS.map((row, i) => {
                      const cell = cells[row.id]
                      const gate = gateOf(cell)
                      return (
                        <tr key={row.id} className={i % 2 === 1 ? 'is-a' : ''}>
                          <td className="is-text is-sec">
                            {row.label}
                            {gate && <small>{gate}</small>}
                            {/* The 42/43 engine stamps every result unverified
                                until a practitioner signs the inputs off. */}
                            {cell?.verified === false && (
                              <span className="gst-chip is-hold ml-2">unverified</span>
                            )}
                          </td>
                          {HEADS.map((h) => (
                            <td key={h}>
                              <CellValue cell={cell} head={h} />
                            </td>
                          ))}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* -- 6.1 ---------------------------------------------------- */}
              <h3 className="gst-sub mb-2">6.1 Payment of tax</h3>
              {/* 6.1 renders from the set-off, which ran on whatever credit was
                  available — and with no 2B that is zero, so this block would
                  otherwise state the whole liability as payable in cash with
                  complete confidence. 4A already says the credit was never
                  established; this stops the figures below it from quietly
                  saying otherwise. */}
              {stateOf(cells['4A']) && (
                <Alert tone="warning" className="mb-3">
                  These figures assume no input credit at all, because none has
                  been established for this period — see 4(A) above. Import the
                  2B to see what is actually payable.
                </Alert>
              )}
              {!setoff ? (
                <div className="gst-empty">No set-off computed for this period.</div>
              ) : (
                <div className="gst-table-wrap mb-5">
                  <table className="gst-table">
                    <thead>
                      <tr>
                        <th className="is-text">Description</th>
                        {HEADS.map((h) => (
                          <th key={h}>{HEAD_LABELS[h]}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="is-text is-sec">Paid through ITC</td>
                        {HEADS.map((h) => (
                          <td key={h}>
                            {rupees(
                              Object.values(setoff.utilisation ?? {}).reduce(
                                (acc, byHead) => acc + (byHead?.[h] ?? 0),
                                0,
                              ),
                            )}
                          </td>
                        ))}
                      </tr>
                      <tr className="is-a">
                        <td className="is-text is-sec">Paid in cash</td>
                        {HEADS.map((h) => (
                          <td key={h}>{rupees(setoff.cash?.[h])}</td>
                        ))}
                      </tr>
                      <tr className="is-net">
                        <td className="is-text is-sec">Credit carried forward</td>
                        {HEADS.map((h) => (
                          <td key={h}>{rupees(setoff.credit_remaining?.[h])}</td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* D-37 — the per-head net, BESIDE 88A and visibly not part of the
                  return. Its own table and its own heading, because a row
                  inside the 6.1 table would read as one more figure to file. */}
              {netView && (
                <div className="mb-5">
                  <p className="gst-panel-h mb-1">
                    Per-head net — for tying to your working, not filed
                    {netView.differences.length > 0 ? (
                      <span className="gst-tag">differs from 6.1</span>
                    ) : (
                      <span className="gst-tag is-clear">same as 6.1</span>
                    )}
                  </p>
                  <p className="gst-meta mb-2">{netView.note}</p>
                  <div className="gst-table-wrap">
                    <table className="gst-table">
                      <thead>
                        <tr>
                          <th className="is-text">Each head on its own</th>
                          {HEADS.map((h) => (
                            <th key={h}>{HEAD_LABELS[h]}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="is-text is-sec">Cash, per head</td>
                          {HEADS.map((h) => (
                            <td key={h}>{rupees(netView.cash[h])}</td>
                          ))}
                        </tr>
                        <tr>
                          <td className="is-text is-sec">Credit carried forward, per head</td>
                          {HEADS.map((h) => (
                            <td key={h}>{rupees(netView.creditRemaining[h])}</td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {netView.differences.length > 0 && (
                    <p className="gst-meta mt-2">
                      A working that nets each head shows more cash than 6.1 by{' '}
                      {netView.differences
                        .map((d) => `₹${rupees(d.paise)} ${HEAD_LABELS[d.head]}`)
                        .join(', ')}{' '}
                      — credit Rule 88A lets another head use.
                    </p>
                  )}
                </div>
              )}

              {/* -- tie-out ------------------------------------------------ */}
              <Alert tone={body.tieout.ok ? 'success' : 'warning'}>
                <p className="gst-panel-h">
                  Tie-out with GSTR-1
                  <span className={`gst-tag ${body.tieout.ok ? 'is-clear' : ''}`}>
                    {body.tieout.ok ? 'reconciles' : `${body.tieout.discrepancies.length} to review`}
                  </span>
                </p>
                {body.tieout.ok ? (
                  <p className="text-sm">GSTR-1 and GSTR-3B outward figures reconcile.</p>
                ) : (
                  <ul className="gst-panel-list">
                    {body.tieout.discrepancies.map((d, i) => (
                      <li key={i}>
                        <span className="gst-k">3.1</span>
                        <span>{String(d.message ?? JSON.stringify(d))}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Alert>
            </>
          )}
        </section>
      </div>
    </div>
  )
}
